// Intercepts API errors; on access-token expiry refreshes then retries once.
import { HttpHandler,
         HttpRequest, HttpErrorResponse,
         HttpInterceptor, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { ERROR } from '@app/constants/Error.constant';
import { AuthService } from '@app/service/auth.service';

/**
 * @desc   - HTTP interceptor: on 401 with access-token expiry code, refreshes
 *            the session and retries the request once; 403 never logs out;
 *            other 401s log out or rethrow for callers.
 */
@Injectable({ providedIn: 'root' })
export class ErrorInterceptor implements HttpInterceptor
{
    authService: AuthService;

    /**
     * @desc   - Stores the injector for lazy AuthService resolution in
     *            intercept to avoid circular DI.
     * @access - public
     * @param  - injector : Angular Injector
     * @return - void
     */
    constructor(private injector: Injector)
    {
    }

    /**
     * @desc   - True when the server returned 401 with body code
     *            ACCESS_TOKEN_EXPIRED, the call is not the refresh or login
     *            URL, and this request has not already been retried after
     *            refresh.
     * @access - private
     * @param  - errorResponse : failed HTTP response
     * @param  - request : request that failed
     * @return - boolean
     */
    private shouldAttemptAccessTokenRefresh
    (
        errorResponse: HttpErrorResponse,
        request: HttpRequest<any>
    ): boolean
    {
        let loginUrl;
        let result;

        loginUrl = `${environment.apiUrl}/user/login`;
        result =
            errorResponse.status === 401 &&
            Boolean(errorResponse.error) &&
            errorResponse.error.code === ERROR.ACCESS_TOKEN_EXPIRED &&
            request.url.indexOf('token/refresh') < 0 &&
            errorResponse.url !== loginUrl &&
            !request.headers.get('x-token-refresh-tried');

        return result;
    }

    /**
     * @desc   - Handles errors when no token refresh is attempted: 400 and
     *            some 401s are rethrown; other 401s log the user out; 403 is
     *            rethrown for callers (for example note permission denied);
     *            500, 503, and 0 are logged. Always returns a stream that
     *            errors with the HTTP status (or rethrows as implemented).
     * @access - public
     * @param  - errorResponse : failed HTTP response
     * @param  - request : original request when available
     * @param  - next : next handler (reserved; not used in current logic)
     * @return - Observable that errors with the response status
     */
    handleResponseError
    (
        errorResponse: HttpErrorResponse,
        request?: HttpRequest<any>,
        next?: HttpHandler
    )
    {
        let loginUrl;
        let prefUrl: string;

        loginUrl = `${environment.apiUrl}/user/login`;

        if (errorResponse.status === 400)
        {
            throw throwError(() => errorResponse.status);
        }
        else if (errorResponse.status === 401)
        {
            prefUrl = `${environment.apiUrl}/preference/getPreferences`;

            if (errorResponse.url === loginUrl)
            {
                return throwError(() => errorResponse.status);
            }

            if (request && request.url === prefUrl)
            {
                return throwError(() => errorResponse.status);
            }

            this.authService.logout();

            return throwError(() => errorResponse.status);
        }
        else if (errorResponse.status === 403)
        {
            return throwError(() => errorResponse.status);
        }
        else if (errorResponse.status === 500)
        {
            console.log('Access denied error.');
        }
        else if (errorResponse.status === 503)
        {
            console.log('Access denied error.');
        }
        else if (errorResponse.status === 0)
        {
            console.log('Cypher server pay is down.');
        }

        return throwError(() => errorResponse.status);
    }

    /**
     * @desc   - Passes the request through; on failure, refreshes and retries
     *            once when shouldAttemptAccessTokenRefresh is true, else
     *            delegates to handleResponseError.
     * @access - public
     * @param  - request : outgoing HTTP request
     * @param  - next : next interceptor/handler in the chain
     * @return - Observable of the HTTP event stream
     */
    intercept
    (
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<any>
    {
        this.authService = this.injector.get(AuthService);

        return next.handle(request).pipe(
            catchError((errorResponse: HttpErrorResponse) =>
            {
                if (this.shouldAttemptAccessTokenRefresh(errorResponse, request))
                {
                    return this.authService.refreshToken().pipe(
                        switchMap(() =>
                        {
                            let retryReq;

                            retryReq = request.clone(
                            {
                                setHeaders:
                                {
                                    'x-token-refresh-tried': '1'
                                }
                            });

                            return next.handle(retryReq);
                        }),
                        catchError(() =>
                        {
                            this.authService.logout();

                            return throwError(() => 401);
                        })
                    );
                }

                return this.handleResponseError(errorResponse, request, next);
            })
        );
    }
}
